import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link,
  Typography,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, ArrowForward } from '@mui/icons-material';
import { GoogleLogin } from '@react-oauth/google';
import { loginSchema } from '../schemas/loginSchema';
import { useAuth } from '../../../hooks/useAuth';
import { extractErrorMessage } from '../../../utils/errorUtils';

const LoginForm = () => {
  const { login, loginWithGoogle } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setError('');
      await loginWithGoogle(credentialResponse.credential);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError('');
      await login(values);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ email: '', password: '', rememberMe: false }}
      validationSchema={loginSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting, values, setFieldValue }) => (
        <Form>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

            <Box>
              <Typography variant="body2" fontWeight={500} gutterBottom sx={{ mb: 1 }}>
                Correo corporativo
              </Typography>
              <Field
                as={TextField}
                name="email"
                type="email"
                fullWidth
                placeholder="nombre@empresa.com"
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                autoComplete="email"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Email sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                  },
                }}
              />
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={500} gutterBottom sx={{ mb: 1 }}>
                Contraseña
              </Typography>
              <Field
                as={TextField}
                name="password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                placeholder="••••••••"
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.rememberMe}
                    onChange={(e) => setFieldValue('rememberMe', e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Recordarme
                  </Typography>
                }
              />
              <Link
                href="/forgot-password"
                underline="hover"
                variant="body2"
                sx={{ color: 'primary.main', fontWeight: 500 }}
              >
                ¿Olvidó su contraseña?
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isSubmitting}
              endIcon={isSubmitting ? null : <ArrowForward />}
              sx={{
                py: 1.625,
                fontSize: '0.9375rem',
                fontWeight: 700,
                borderRadius: 0,
                bgcolor: '#5980a6',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#416180',
                  boxShadow: 'none',
                },
                '&:active': {
                  boxShadow: 'none',
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Iniciar Sesión'
              )}
            </Button>

            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" sx={{ color: '#98989b', px: 1 }}>
                o continúa con
              </Typography>
            </Divider>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Error al iniciar sesión con Google')}
                theme="outline"
                size="large"
                width="100%"
                text="signin_with"
                locale="es"
              />
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
